// On the website, the API routes live at the same origin as the app, so a
// relative path like "/api/ai-insights" works. In the native iOS/Android app
// the web bundle is served from capacitor://localhost (there is no backend
// there), so relative /api calls fail. When running natively we must call the
// deployed production API by its absolute URL instead.
//
// @capacitor/core is a hard dependency (bundled in both web and native builds);
// its CapacitorHttp/Capacitor exports carry web fallbacks, so importing them
// here is safe for the website too — `postStreamedJson` only takes the native
// branch when Capacitor.isNativePlatform() is true.
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { auth } from '../firebase';

const NATIVE_API_ORIGIN = 'https://www.dugoutbaseball.app';

function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

export function apiUrl(path: string): string {
  return isNativePlatform() ? `${NATIVE_API_ORIGIN}${path}` : path;
}

/**
 * Fetch an ICS calendar feed through our server-side proxy. The proxy requires
 * a Firebase login token (it fetches on the caller's behalf, so it must not be
 * an open relay) — attach it here so every caller gets it right.
 */
export async function fetchProxiedIcs(icsUrl: string): Promise<Response> {
  const token = await auth.currentUser?.getIdToken();
  return fetch(apiUrl(`/api/proxy-ics?url=${encodeURIComponent(icsUrl)}`), {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

// ── Heartbeat-streamed Claude endpoints ──────────────────────────────────────
// /api/game-insights and /api/ai-insights both call the Claude API (~15-30s).
// A normal buffered response holds one connection open and idle for that whole
// window, and some iOS network paths reset an idle long-lived connection before
// the response arrives — surfacing as "Load failed" / "network connection was
// lost". So those endpoints flush a 200 immediately, emit a newline heartbeat
// every 2s while Claude works (so bytes never stop flowing and the connection is
// never idle), then write the FINAL JSON result (or an {error} object) on the
// last line. This helper reads the whole body and parses that last non-empty
// line — the shared, on-device-validated path for both callers.

/** Take the last non-empty line of a heartbeat-streamed body and JSON-parse it. */
export function parseStreamedBody(raw: unknown): any {
  // CapacitorHttp may hand back an already-parsed object (if it happened to
  // parse) or the raw string. Normalize to the final JSON line.
  if (raw && typeof raw === 'object') return raw;
  const text = typeof raw === 'string' ? raw : String(raw ?? '');
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const last = lines[lines.length - 1] || '{}';
  try { return JSON.parse(last); } catch { return {}; }
}

/**
 * POST JSON to a heartbeat-streamed endpoint and return the parsed final line.
 *
 * NATIVE (iOS/Android): use CapacitorHttp, which performs the request on the
 * native HTTP stack (URLSession on iOS) — NOT through the WKWebView's fetch/XHR.
 * In the WKWebView, Firestore `channel` churn destabilizes the shared connection
 * pool and kills the long-running Claude request mid-flight (confirmed on-device:
 * failed at ~5.4s). CapacitorHttp leaves that layer entirely. CORS doesn't apply
 * to native HTTP, so no preflight is involved. We read the streamed body as text
 * (responseType:'text') so heartbeat newlines don't trip JSON auto-parse.
 *
 * WEB: keep fetch (same-origin on the website; works fine in real browsers).
 *
 * On an HTTP error status (or an {error} final line), the result is tagged with
 * __httpError so the caller can surface the message.
 */
export async function postStreamedJson(
  url: string,
  headers: Record<string, string>,
  body: string,
  logTag = 'api',
): Promise<any> {
  if (isNativePlatform()) {
    try {
      // readTimeout covers the full Claude wait; connectTimeout is the handshake.
      const res = await CapacitorHttp.post({
        url,
        headers,
        data: JSON.parse(body),
        responseType: 'text',
        readTimeout: 60000,
        connectTimeout: 15000,
      });
      const data = parseStreamedBody(res.data);
      // The endpoint always returns 200 now (it flushes before knowing the
      // result); failures arrive as an {error} object on the final line.
      if ((res.status < 200 || res.status >= 300) || data?.error) {
        return { __httpError: true, ...data };
      }
      return data;
    } catch (err: any) {
      const detail = `name=${err?.name ?? '?'} | msg=${err?.message ?? String(err)} | url=${url}`;
      console.error(`[${logTag}] CapacitorHttp failed:`, detail, err);
      throw new Error(`Network error: ${detail}`);
    }
  }

  // Web: standard fetch; read text and parse the final line (same streamed shape).
  try {
    const res = await fetch(url, { method: 'POST', headers, body });
    const text = await res.text().catch(() => '');
    const data = parseStreamedBody(text);
    if (!res.ok || data?.error) return { __httpError: true, ...data };
    return data;
  } catch (err: any) {
    const detail = `name=${err?.name ?? '?'} | msg=${err?.message ?? String(err)} | url=${url}`;
    console.error(`[${logTag}] fetch failed:`, detail, err);
    throw new Error(`Network error: ${detail}`);
  }
}
